import React, { useContext, useState, useEffect, useRef } from "react";
import _, { xor } from "lodash";
import { VariantContextData } from "../variants/VariantBuilderContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatSpacingValueInput } from "../../utils/builderUtils";

const VariantBuilderSettingSpacing = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const [selectedSpacingType, selectSpacingType] = useState(null);
  const [selectedSide, selectSide] = useState(null);
  const setting_name = props.setting_name;
  const node = useRef(null);
  const skipNextBlurCommitRef = useRef(false);
  const handleClickOutsideRef = useRef(null);

  function commitChangedSpacingInputs() {
    node.current
      ?.querySelectorAll("input.setting-spacing-input")
      .forEach((input) => commitSpacingInput(input));
  }

  function commitSpacingInput(input) {
    if (!input?.classList?.contains("setting-spacing-input")) {
      return;
    }

    if (!node.current?.contains(input)) {
      return;
    }

    const dataName = input.getAttribute("data-setting_name");
    const spacingTypes = ["margin", "padding"];
    const sides = ["top", "bottom", "left", "right"];
    let spacing_type;
    let side;

    for (const type of spacingTypes) {
      for (const s of sides) {
        const suffix = `${type}${s}`;
        if (dataName?.endsWith(suffix) && dataName.startsWith(setting_name)) {
          spacing_type = type;
          side = s;
          break;
        }
      }

      if (spacing_type) {
        break;
      }
    }

    if (!spacing_type || !side) {
      return;
    }

    const currentSettingName = `${setting_name.replace(
      "SPACING_TYPE",
      spacing_type
    )}${side}]`;
    const storedValue = _.get(props.object, currentSettingName);
    const formatted = formatSpacingValueInput(input.value);
    const stored = formatSpacingValueInput(String(storedValue ?? ""));

    if (formatted === stored) {
      return;
    }

    const parsed_value = (input.value = formatSpacingValueInput(input.value));

    builder.update([
      {
        object_type: props.object_type,
        object_id: props.object_id,
        setting_name: currentSettingName,
        value: parsed_value,
      },
    ]);
  }


  function blurActiveSpacingInput() {
    const active = document.activeElement;
    if (
      active?.classList?.contains("setting-spacing-input") &&
      node.current?.contains(active)
    ) {
      active.blur();
    }
  }

  let currentSettingName;
  let current_value;

  if (selectedSide && selectedSpacingType) {
    currentSettingName =
      setting_name.replace("SPACING_TYPE", selectedSpacingType) +
      selectedSide +
      "]";
    current_value = _.get(props.object, currentSettingName);
  }

  function updateSpacing(spacingType, side, action, setInputValue, valueOverride) {
    let newSettingName =
      setting_name.replace("SPACING_TYPE", spacingType) + side + "]";

    let new_value =
      valueOverride !== undefined
        ? valueOverride
        : _.get(props.object, newSettingName);
    let has_percentage = typeof new_value == "string" && new_value.includes('%') ? true : false

    if ((_.isNil(new_value) || new_value == '') && new_value !== 0 && newSettingName.indexOf("-mobile") > -1) {
      let desktopSettingName = newSettingName.replace("-mobile", "");
      new_value = _.get(props.object, desktopSettingName);
    }

    new_value = new_value || 0;

    if (typeof new_value == "string" && new_value.indexOf("_") > -1) {
      const levelsMap = {
        0: 0,
        1: 15,
        2: 25,
        3: 40,
        4: 70,
        5: 120,
      };

      let level = new_value.split("_").pop();
      new_value = levelsMap[level] || 0;
    } else if (typeof new_value == "string") {
      new_value = parseInt(new_value);
    }

    let increment = 20;

    if (new_value < 60) {
      increment = 10;
    }

    if (new_value < 20) {
      increment = 5;
    }

    if (action == "add") {
      new_value = new_value + increment;
    }

    if (action == "subtract") {
      new_value = new_value - increment;

      if (spacingType == "padding" && new_value < 0) {
        new_value = 0;
      }
    }

    if (has_percentage) {
      new_value = new_value + '%';
    }

    if (new_value !== current_value) {
      builder.update([
        {
          object_type: props.object_type,
          object_id: props.object_id,
          setting_name: newSettingName,
          value: new_value,
          skip_history: props.skip_history,
        },
      ]);
    }

    selectSpacingType(spacingType);
    selectSide(side);
    setInputValue(new_value);
  }

  const StyleControl = ({ spacing_type, side, disabled }) => {
    const inputRef = useRef(null);
    const currentSettingName = `${setting_name.replace("SPACING_TYPE", spacing_type)}${side}]`;
    const value = _.get(props.object, currentSettingName);
    const valueDesktop = _.get(props.object, currentSettingName.replace("-mobile", ""));
    const verticalSpacing = ["top", "bottom"].includes(side);
    const [inputValue, setInputValue] = useState(null);
    const iconVisibility =
      selectedSpacingType == spacing_type &&
      selectedSide == side &&
      disabled !== true
        ? "visible"
        : "hidden";

    function onChange(value, spacing_type, side) {
      const currentSettingName = `${setting_name.replace(
        "SPACING_TYPE",
        spacing_type
      )}${side}]`;

      let parsed_value = inputRef.current.value = formatSpacingValueInput(value);

      builder.update([
        {
          object_type: props.object_type,
          object_id: props.object_id,
          setting_name: currentSettingName,
          value: parsed_value,
        },
      ]);
    }

    function commitInputValue(rawValue) {
      if (rawValue === undefined || rawValue === null) {
        return;
      }

      const formatted = formatSpacingValueInput(rawValue);
      const stored = formatSpacingValueInput(String(value ?? ""));

      if (formatted === stored) {
        return;
      }

      onChange(rawValue, spacing_type, side);
    }

    function spacingValueFromInput() {
      if (!inputRef.current) {
        return undefined;
      }

      const rawValue = inputRef.current.value;
      if (rawValue === "") {
        return undefined;
      }

      return formatSpacingValueInput(rawValue);
    }

    function syncInputValue(newValue) {
      setInputValue(newValue);
      if (inputRef.current) {
        inputRef.current.value = newValue ?? "";
      }
    }

    return (
      <React.Fragment>
        <div
          className={`flex items-center justify-center ${
            verticalSpacing ? "flex-row" : "flex-col-reverse"
          }`}
          data-spacing-type={spacing_type}
          data-side={side}
          onMouseOver={(e) => {
            selectSpacingType(spacing_type);
            selectSide(side);
          }}
        >
          <div
            className={`cursor-pointer -mt-[3px] center`}
            title="Subtract"
            onClick={() => {
              updateSpacing(
                spacing_type,
                side,
                "subtract",
                syncInputValue,
                spacingValueFromInput()
              );
            }}
            style={{
              visibility: iconVisibility,
              width: "20px",
            }}
          >
            <FontAwesomeIcon
              icon="fas fa-minus"
              className="text-sm text-slate-500 dark:text-slate-200"
            />
          </div>
          <div
            className={`flex items-center justify-center ${
              verticalSpacing ? "px-3" : ""
            }`}
          >
            <input
              type="text"
              defaultValue={value}
              placeholder={currentSettingName.indexOf('mobile') > -1 && valueDesktop ? valueDesktop : "0"}
              ref={inputRef}
              min={spacing_type == "padding" ? 0 : null}
              onFocus={(e) => {
                selectSpacingType(spacing_type);
                selectSide(side);
                skipNextBlurCommitRef.current = false;

                setTimeout(() => {
                  document
                    .querySelector(
                      `input[data-setting_name="${props.setting_name}-${spacing_type}${side}"]`
                    )
                    .focus();
                }, 100);
              }}
              onChange={(e) => {
                setInputValue(e.target.value);
              }}
              onBlur={() => {
                if (skipNextBlurCommitRef.current) {
                  skipNextBlurCommitRef.current = false;
                  return;
                }

                commitSpacingInput(inputRef.current);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitInputValue(e.target.value);
                  skipNextBlurCommitRef.current = true;
                  e.target.blur();
                }
              }}
              disabled={disabled}
              className={`setting-spacing-input w-12 text-center bg-transparent text-slate-500 dark:text-slate-200 rounded-md border-none outline-none p-0 text-lg`}
              data-setting_name={`${props.setting_name}-${spacing_type}${side}`}
            />
          </div>
          <div
            className={`cursor-pointer -mt-[3px] ${!verticalSpacing ? 'pb-2' : ''} center`}
            title="Add"
            style={{
              visibility: iconVisibility,
              width: "20px",
            }}
            onClick={() => {
              updateSpacing(
                spacing_type,
                side,
                "add",
                syncInputValue,
                spacingValueFromInput()
              );
            }}
          >
            <FontAwesomeIcon
              icon="fas fa-plus"
              className="text-sm text-slate-500 dark:text-slate-200"
            />
          </div>
        </div>
      </React.Fragment>
    );
  };

  const handleClickOutside = (e) => {
    if (node.current?.contains(e.target)) {
      return;
    }

    const hadFocusedInput =
      document.activeElement?.classList?.contains("setting-spacing-input") &&
      node.current?.contains(document.activeElement);

    commitChangedSpacingInputs();

    if (hadFocusedInput) {
      skipNextBlurCommitRef.current = true;
      document.activeElement.blur();
    }

    if (selectedSide || selectedSpacingType) {
      selectSide(null);
      selectSpacingType(null);
    }
  };

  handleClickOutsideRef.current = handleClickOutside;

  const handleMouseDownInside = (e) => {
    if (
      e.target.closest(
        "input.setting-spacing-input, [title='Add'], [title='Subtract']"
      )
    ) {
      return;
    }

    commitChangedSpacingInputs();
    skipNextBlurCommitRef.current = true;
    blurActiveSpacingInput();
  };

  useEffect(() => {
    const listener = (event) => handleClickOutsideRef.current(event);
    document.addEventListener("mousedown", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
    };
  }, []);

  return (
    <React.Fragment>
      <div
        ref={node}
        className="w-full relative bg-slate-100 dark:bg-slate-800 rounded-lg"
        onMouseDown={handleMouseDownInside}
      >
        <div className="absolute top left mt-2 pl-2 text-slate-600 dark:text-slate-200 font-semibold text-sm">
          MARGIN
        </div>
        <div className="w-full py-2">
          <StyleControl
            spacing_type="margin"
            side="top"
            disabled={props.padding_only == true}
          />
        </div>
        <div className="w-full">
          <div className="flex flex-row items-center justify-between">
            <div className="w-[40px] items-center justify-between">
              <StyleControl
                spacing_type="margin"
                side="left"
                disabled={props.padding_only == true}
              />
            </div>
            <div className="flex-grow bg-white dark:bg-slate-950 rounded-md">
              <div className="w-full mt-2 -mb-4">
                <StyleControl spacing_type="padding" side="top" />
              </div>
              <div className="w-full flex flex-row items-center justify-between">
                <div className="w-[40px] items-center justify-between">
                  <StyleControl spacing_type="padding" side="left" />
                </div>
                <div className="flex-grow text-slate-600 dark:text-slate-200 text-sm font-semibold text-center pt-4 pb-3">
                  PADDING
                </div>
                <div className="w-[40px] items-center justify-between">
                  <StyleControl spacing_type="padding" side="right" />
                </div>
              </div>
              <div className="w-full -mt-4 mb-2">
                <StyleControl spacing_type="padding" side="bottom" />
              </div>
            </div>
            <div className="w-[40px] items-center justify-between">
              <StyleControl
                spacing_type="margin"
                side="right"
                disabled={props.padding_only == true}
              />
            </div>
          </div>
        </div>
        <div className="w-full py-2">
          <StyleControl
            spacing_type="margin"
            side="bottom"
            disabled={props.padding_only == true}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default VariantBuilderSettingSpacing;
